/**
 * GIF frame-delay rewriting for the "re-encode with 100 ms frames" fix of PH-04 (W2-12a checker-runtime;
 * DESIGN.md §7.6; formats-and-packaging.md P§2.7; skin-and-assets.md: Phira reads the Graphics Control Extension
 * delay (x 10 ms) of every frame and hangs when they add up to 0).
 *
 * `setGifFrameDelays(bytes, delayMs)` walks the GIF block structure (header, logical screen descriptor, global
 * colour table, extension blocks, image descriptors with their local colour tables and LZW data, trailer), sets the
 * delay of every Graphics Control Extension and inserts one (no transparency, no disposal) before every frame that
 * has none. Pixels, palettes and every other block are copied byte for byte. A GIF87a header becomes GIF89a (the
 * version that has extensions). Throws `GifFormatError` on input that is not a well-formed GIF; its text is for the
 * console (media.ts turns it into a MediaFixError of kind 'gif', which the fix shows as a localised message).
 */

export class GifFormatError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GifFormatError';
  }
}

export interface GifRewrite {
  readonly bytes: Uint8Array;
  readonly frames: number;
  /** Delay written for every frame, in ms (a multiple of 10: GIF delays are hundredths of a second). */
  readonly delayMs: number;
}

const EXTENSION = 0x21;
const IMAGE = 0x2c;
const TRAILER = 0x3b;
const GCE_LABEL = 0xf9;

class Writer {
  private buf: Uint8Array;
  private len = 0;
  constructor(capacity: number) {
    this.buf = new Uint8Array(Math.max(64, capacity));
  }
  private ensure(n: number): void {
    if (this.len + n <= this.buf.length) return;
    let cap = this.buf.length * 2;
    while (cap < this.len + n) cap *= 2;
    const next = new Uint8Array(cap);
    next.set(this.buf.subarray(0, this.len));
    this.buf = next;
  }
  bytes(src: Uint8Array): void {
    this.ensure(src.length);
    this.buf.set(src, this.len);
    this.len += src.length;
  }
  byte(b: number): void {
    this.ensure(1);
    this.buf[this.len++] = b & 0xff;
  }
  result(): Uint8Array {
    return this.buf.slice(0, this.len);
  }
}

/** Index just after the data sub-blocks starting at `i` (size byte + data, until a zero size byte). */
function skipSubBlocks(src: Uint8Array, i: number): number {
  let p = i;
  for (;;) {
    if (p >= src.length) throw new GifFormatError('GIF data ends inside a block');
    const size = src[p] as number;
    p += 1;
    if (size === 0) return p;
    p += size;
  }
}

/** Rewrites every frame delay of a GIF to `delayMs` (rounded to hundredths of a second, at least 10 ms). */
export function setGifFrameDelays(src: Uint8Array, delayMs: number): GifRewrite {
  const cs = Math.max(1, Math.min(0xffff, Math.round(delayMs / 10)));
  if (src.length < 13) throw new GifFormatError('not a GIF file');
  const sig = String.fromCharCode(...src.subarray(0, 6));
  if (sig !== 'GIF87a' && sig !== 'GIF89a') throw new GifFormatError('not a GIF file');
  const out = new Writer(src.length + 64);
  out.bytes(new Uint8Array([0x47, 0x49, 0x46, 0x38, 0x39, 0x61])); // GIF89a
  // Logical screen descriptor (7 bytes) and the global colour table.
  const packed = src[10] as number;
  let p = 13;
  if ((packed & 0x80) !== 0) p += 3 * (1 << ((packed & 0x07) + 1));
  if (p > src.length) throw new GifFormatError('GIF data ends inside the colour table');
  out.bytes(src.subarray(6, p));

  let frames = 0;
  let pendingGce = false;
  for (;;) {
    if (p >= src.length) throw new GifFormatError('GIF data ends without a trailer');
    const block = src[p] as number;
    if (block === TRAILER) {
      out.byte(TRAILER);
      break;
    }
    if (block === EXTENSION) {
      const label = src[p + 1];
      if (label === undefined) throw new GifFormatError('GIF data ends inside an extension');
      if (label === GCE_LABEL) {
        // 21 F9 04 <packed> <delay lo> <delay hi> <transparent index> 00
        const size = src[p + 2];
        if (size !== 4 || p + 8 > src.length) throw new GifFormatError('malformed graphics control extension');
        out.bytes(
          new Uint8Array([EXTENSION, GCE_LABEL, 4, src[p + 3] as number, cs & 0xff, cs >> 8, src[p + 6] as number, 0]),
        );
        p = skipSubBlocks(src, p + 2);
        pendingGce = true;
        continue;
      }
      const end = skipSubBlocks(src, p + 2);
      out.bytes(src.subarray(p, end));
      p = end;
      continue;
    }
    if (block === IMAGE) {
      if (p + 10 > src.length) throw new GifFormatError('GIF data ends inside an image descriptor');
      if (!pendingGce) out.bytes(new Uint8Array([EXTENSION, GCE_LABEL, 4, 0, cs & 0xff, cs >> 8, 0, 0]));
      const local = src[p + 9] as number;
      let q = p + 10;
      if ((local & 0x80) !== 0) q += 3 * (1 << ((local & 0x07) + 1));
      q += 1; // LZW minimum code size
      if (q > src.length) throw new GifFormatError('GIF data ends inside an image');
      const end = skipSubBlocks(src, q);
      out.bytes(src.subarray(p, end));
      p = end;
      frames++;
      pendingGce = false;
      continue;
    }
    throw new GifFormatError(`unknown GIF block 0x${block.toString(16)}`);
  }
  if (frames === 0) throw new GifFormatError('the GIF has no frames');
  return { bytes: out.result(), frames, delayMs: cs * 10 };
}
